/**
 * Redirect URL Validation Utility
 * Prevents open redirect vulnerabilities by validating redirect URLs
 */

const DEFAULT_REDIRECT = '/projects';

/**
 * Allowlist of safe redirect paths
 * Add new paths here as needed
 */
const ALLOWED_REDIRECT_PATHS = [
  '/projects',
  '/project/',
  '/documents',
  '/reports',
  '/users',
  '/changePassword',
  '/preLogin',
  '/login',
];

/**
 * Validates a redirect URL to prevent open redirect attacks
 * 
 * Security checks:
 * - Rejects absolute URLs (http://, https://, //)
 * - Rejects dangerous protocols (javascript:, data:, vbscript:)
 * - Only allows internal paths starting with /
 * - Validates against allowlist of known safe paths
 * 
 * @param {string} url - The redirect URL to validate
 * @param {string} defaultPath - Default path if validation fails (default: '/projects')
 * @returns {string} - Safe validated redirect URL
 */
export const validateRedirectUrl = (url, defaultPath = DEFAULT_REDIRECT) => {
  // Handle null, undefined, or non-string values
  if (!url || typeof url !== 'string') {
    return defaultPath;
  }

  const trimmedUrl = url.trim();
  
  // Reject empty or whitespace-only URLs
  if (!trimmedUrl) {
    return defaultPath;
  }

  // Reject absolute URLs and dangerous protocols
  const dangerousPatterns = [
    'http://',
    'https://',
    '//',
    '://',
    ['java', 'script:'].join(''), // Avoid linter warning for javascript:
    'data:',
    ['vb', 'script:'].join(''), // Avoid linter warning for vbscript:
    'file:',
    'about:',
  ];

  const isDangerous = dangerousPatterns.some(pattern => 
    trimmedUrl.toLowerCase().startsWith(pattern) || 
    trimmedUrl.toLowerCase().includes(pattern)
  );

  if (isDangerous) {
    console.warn('[Redirect Validation] Rejected dangerous URL:', trimmedUrl);
    return defaultPath;
  }

  // Only allow internal paths starting with /
  if (!trimmedUrl.startsWith('/')) {
    console.warn('[Redirect Validation] Rejected non-path URL:', trimmedUrl);
    return defaultPath;
  }

  // Validate against allowlist
  const isAllowed = ALLOWED_REDIRECT_PATHS.some(path => 
    trimmedUrl.startsWith(path)
  );
  
  if (!isAllowed) {
    console.warn('[Redirect Validation] Path not in allowlist:', trimmedUrl);
    return defaultPath;
  }

  return trimmedUrl;
};

/**
 * Safely redirects to a validated URL
 * @param {string} url - The URL to redirect to
 * @param {string} defaultPath - Default path if validation fails
 */
export const safeRedirect = (url, defaultPath = DEFAULT_REDIRECT) => {
  const safeUrl = validateRedirectUrl(url, defaultPath);
  window.location.href = safeUrl;
};

export default {
  validateRedirectUrl,
  safeRedirect,
  ALLOWED_REDIRECT_PATHS,
};
